import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Loader, Trash2, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Select } from '../common/Select.js';
import {
  createFlowchartSubprocessLinks,
  deleteFlowchartSubprocessLink,
  fetchFlowchartSubprocessLinks,
  fetchSubprocessNodeCatalog,
  formatSubprocessNodeLabel,
} from '../../request/flowchart/subprocessLinks.api';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';

const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 120 }),
  menu: (base) => ({ ...base, zIndex: 120 }),
};

function formatSourceNodeLabel(node) {
  const description = String(node?.nodeDescription || '').trim() || 'Untitled step';
  const actor = String(node?.actor || '').trim();
  return actor ? `${description} · ${actor}` : description;
}

function formatCatalogProcessLabel(process) {
  const processId = String(process?.processId || process?.processUuid || 'Process').trim();
  const name = String(process?.name || '').trim();
  return name ? `${processId} · ${name}` : processId;
}

function formatCatalogVersionLabel(process) {
  const number = Number(process?.versionNumber);
  const versionText = Number.isFinite(number) ? `v${number}` : 'Version';
  const status = String(process?.versionStatus || '').trim().toLowerCase();
  if (status === 'published' && process?.isActiveVersion === true) {
    return `${versionText} · Published (active)`;
  }
  if (status === 'draft') {
    return `${versionText} · Draft`;
  }
  return versionText;
}

export default function FlowchartSubprocessLinksSidePanel({
  isOpen,
  onClose,
  cycleId,
  processUuid,
  processDisplayId = '',
  flowchartVersionId,
  currentProcessNodes = [],
  existingLinks = [],
  onLinksChanged,
}) {
  const [links, setLinks] = useState([]);
  const [catalogProcesses, setCatalogProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState('');
  const [sourceNodeId, setSourceNodeId] = useState('');
  const [selectedTargetProcessUuid, setSelectedTargetProcessUuid] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);

  const sourceOptions = useMemo(
    () =>
      currentProcessNodes.map((node) => ({
        value: node.id,
        label: formatSourceNodeLabel(node),
      })),
    [currentProcessNodes]
  );

  const processOptions = useMemo(
    () =>
      catalogProcesses.map((process) => ({
        value: process.processUuid,
        label: formatCatalogProcessLabel(process),
      })),
    [catalogProcesses]
  );

  const selectedCatalogProcess = useMemo(
    () =>
      catalogProcesses.find(
        (process) => String(process.processUuid) === String(selectedTargetProcessUuid)
      ) || null,
    [catalogProcesses, selectedTargetProcessUuid]
  );

  const targetOptions = useMemo(() => {
    if (!selectedCatalogProcess) return [];
    return (Array.isArray(selectedCatalogProcess.nodes) ? selectedCatalogProcess.nodes : []).map(
      (node) => ({
        value: `${selectedCatalogProcess.processUuid}:${node.id}`,
        label: formatSubprocessNodeLabel(node),
        targetProcessId: selectedCatalogProcess.processUuid,
        targetNodeId: node.id,
      })
    );
  }, [selectedCatalogProcess]);

  const connectedTargetKeys = useMemo(() => {
    const keys = new Set();
    if (!sourceNodeId) return keys;
    links.forEach((link) => {
      if (String(link.source_node_id) !== String(sourceNodeId)) return;
      const processId = String(link.target_process_id || '').trim();
      const nodeId = String(link.target_node_id || '').trim();
      if (processId && nodeId) keys.add(`${processId}:${nodeId}`);
    });
    return keys;
  }, [links, sourceNodeId]);

  const availableTargetOptions = useMemo(
    () => targetOptions.filter((option) => !connectedTargetKeys.has(String(option.value))),
    [targetOptions, connectedTargetKeys]
  );

  const visibleLinks = useMemo(() => {
    const nodeIds = new Set(
      (Array.isArray(currentProcessNodes) ? currentProcessNodes : []).map((node) => String(node.id))
    );
    return (Array.isArray(links) ? links : []).filter((link) =>
      nodeIds.has(String(link?.source_node_id || link?.sourceNodeId || ''))
    );
  }, [links, currentProcessNodes]);

  const loadLinks = useCallback(async ({ silent = false } = {}) => {
    if (!flowchartVersionId) {
      setLinks([]);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const nextLinks = await fetchFlowchartSubprocessLinks(flowchartVersionId);
      setLinks(nextLinks);
      onLinksChanged?.(nextLinks);
    } catch (err) {
      setLinks([]);
      onLinksChanged?.([]);
      toast.error(err?.message || 'Failed to load subprocess links');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [flowchartVersionId, onLinksChanged]);

  const loadCatalog = useCallback(async () => {
    if (!cycleId) {
      setCatalogProcesses([]);
      return;
    }
    setLoadingCatalog(true);
    try {
      const processes = await fetchSubprocessNodeCatalog(cycleId, processUuid);
      setCatalogProcesses(processes);
    } catch (err) {
      setCatalogProcesses([]);
      toast.error(err?.message || 'Failed to load cycle subprocess nodes');
    } finally {
      setLoadingCatalog(false);
    }
  }, [cycleId, processUuid]);

  useEffect(() => {
    setLinks(Array.isArray(existingLinks) ? existingLinks : []);
  }, [existingLinks]);

  useEffect(() => {
    if (!isOpen) return;
    loadCatalog();
  }, [isOpen, loadCatalog]);

  useEffect(() => {
    if (!isOpen) {
      setSourceNodeId('');
      setSelectedTargetProcessUuid('');
      setSelectedTargets([]);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedTargets((prev) =>
      prev.filter((value) => !connectedTargetKeys.has(String(value)))
    );
  }, [connectedTargetKeys]);

  const handleConnect = useCallback(async () => {
    if (!flowchartVersionId || !sourceNodeId || selectedTargets.length === 0) {
      toast.error('Select a source node and at least one target node');
      return;
    }
    setSaving(true);
    try {
      const optionByValue = new Map(targetOptions.map((option) => [String(option.value), option]));
      const targets = selectedTargets
        .map((entry) => {
          const option = optionByValue.get(String(entry));
          if (option?.targetProcessId && option?.targetNodeId) {
            return {
              targetProcessId: option.targetProcessId,
              targetNodeId: option.targetNodeId,
            };
          }
          const raw = String(entry || '');
          const separator = raw.indexOf(':');
          if (separator <= 0) return null;
          return {
            targetProcessId: raw.slice(0, separator),
            targetNodeId: raw.slice(separator + 1),
          };
        })
        .filter((target) => target?.targetProcessId && target?.targetNodeId);
      if (targets.length === 0) {
        toast.error('Select a source node and at least one target node');
        setSaving(false);
        return;
      }
      const result = await createFlowchartSubprocessLinks({
        flowchartVersionId,
        sourceNodeId,
        targets,
      });
      const createdCount = Array.isArray(result?.created_ids) ? result.created_ids.length : 0;
      const skippedCount = Array.isArray(result?.skipped_ids) ? result.skipped_ids.length : 0;
      if (createdCount > 0) {
        toast.success(
          `Connected ${createdCount} subprocess link${createdCount > 1 ? 's' : ''}`
        );
      } else if (skippedCount > 0) {
        toast.error('Selected links already exist');
      }
      setSelectedTargets([]);
      await loadLinks({ silent: true });
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err?.message || 'Failed to connect subprocess links'));
    } finally {
      setSaving(false);
    }
  }, [flowchartVersionId, sourceNodeId, selectedTargets, targetOptions, loadLinks]);

  const handleDelete = useCallback(
    async (linkId) => {
      if (!linkId) return;
      setDeletingLinkId(linkId);
      try {
        await deleteFlowchartSubprocessLink(linkId);
        toast.success('Subprocess link removed');
        await loadLinks({ silent: true });
      } catch (err) {
        toast.error(resolve403ErrorMessage(err, err?.message || 'Failed to remove subprocess link'));
      } finally {
        setDeletingLinkId('');
      }
    },
    [loadLinks]
  );

  if (!isOpen) return null;

  return (
    <aside className="flex h-full min-h-0 w-[460px] shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-white">
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Cross-process links
          </p>
          <h2 className="text-base font-semibold text-slate-900">All subprocesses</h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Connect a node on this flowchart version to nodes on other cycle processes.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close subprocess panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="shrink-0 space-y-3 border-b border-slate-200 px-4 py-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            This process node
          </label>
          <Select
            value={sourceNodeId}
            onChange={(value) => setSourceNodeId(value || '')}
            options={sourceOptions}
            isSearchable
            placeholder="Select node on this flowchart..."
            placeholderOnFocus="Type to search this process node..."
            isClearable
            styles={SELECT_STYLES}
            menuPortalTarget={document.body}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            Other process
          </label>
          <Select
            value={selectedTargetProcessUuid}
            onChange={(value) => {
              setSelectedTargetProcessUuid(value || '');
              setSelectedTargets([]);
            }}
            options={processOptions}
            isSearchable
            isClearable
            placeholder={
              loadingCatalog
                ? 'Loading cycle processes...'
                : processOptions.length === 0
                  ? 'No other processes available'
                  : 'Select a process...'
            }
            placeholderOnFocus="Type to search other processes..."
            isDisabled={loadingCatalog || processOptions.length === 0}
            styles={SELECT_STYLES}
            menuPortalTarget={document.body}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            Other process nodes
          </label>
          {selectedCatalogProcess ? (
            <p className="mb-1.5 text-[11px] text-slate-500">
              Using {selectedCatalogProcess.processId || selectedCatalogProcess.processUuid} ·{' '}
              {formatCatalogVersionLabel(selectedCatalogProcess)}
            </p>
          ) : null}
          <Select
            isMulti
            isSearchable
            value={selectedTargets}
            onChange={(values) => setSelectedTargets(Array.isArray(values) ? values : [])}
            options={availableTargetOptions}
            placeholder={
              !selectedTargetProcessUuid
                ? 'Select a process first...'
                : availableTargetOptions.length === 0
                  ? 'No other nodes available'
                  : 'Select one or more target nodes...'
            }
            placeholderOnFocus="Type to search other process nodes..."
            isDisabled={!selectedTargetProcessUuid || availableTargetOptions.length === 0}
            styles={SELECT_STYLES}
            menuPortalTarget={document.body}
          />
        </div>
        <button
          type="button"
          onClick={handleConnect}
          disabled={saving || !sourceNodeId || selectedTargets.length === 0}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          <Link2 className="h-4 w-4" />
          {saving ? 'Connecting...' : 'Connect'}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Loading links...
          </div>
        ) : visibleLinks.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">
            No subprocess links yet for this flowchart version.
          </p>
        ) : (
          <div className="space-y-3">
            {visibleLinks.map((link) => {
              const sourceNode = currentProcessNodes.find(
                (node) => String(node.id) === String(link.source_node_id)
              );
              const fromProcessId = String(processDisplayId || '').trim();
              const toProcessId = String(
                link.target_process_display_id || link.target_process_name || ''
              ).trim();
              return (
                <div
                  key={link.id}
                  className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 shadow-sm"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        From
                      </p>
                      {fromProcessId ? (
                        <p className="mt-0.5">
                          <span className="inline-flex items-center rounded-md bg-teal-50 px-1.5 py-0.5 text-[11px] font-semibold text-teal-700 ring-1 ring-inset ring-teal-200">
                            {fromProcessId}
                          </span>
                        </p>
                      ) : null}
                      <p className="text-sm font-medium text-slate-900">
                        {formatSourceNodeLabel(sourceNode)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(link.id)}
                      disabled={deletingLinkId === link.id}
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-red-500 transition-colors hover:bg-red-50"
                      aria-label="Remove subprocess link"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      To
                    </p>
                    <p className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                      {toProcessId ? (
                        <span className="inline-flex items-center rounded-md bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-inset ring-red-200">
                          {toProcessId}
                        </span>
                      ) : null}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {formatSubprocessNodeLabel({
                        nodeDescription: link.target_node_description,
                        nodeType: link.target_node_type,
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
